/**
 * Cross-platform information architecture contract.
 * Web (`navigationConfig.ts`) and Expo (`SALTAppMobile/.../navigationConfig.ts`)
 * must stay aligned with these ids and primary paths.
 *
 * @see docs/REDESIGN_CHARTER.md
 */

/**
 * Canonical five-slot member bottom nav (Expo default).
 * Slot 4 is Messages until mobile Journal ships; web may swap to Journal via `resolveSlot4NavId`.
 */
export const MEMBER_BOTTOM_NAV_IDS = ['home', 'groups', 'tasks', 'messages', 'profile'] as const

export type MemberBottomNavId = (typeof MEMBER_BOTTOM_NAV_IDS)[number]

/** Slot-4 candidates — mutually exclusive in the five-tab bar */
export const SLOT_4_NAV_IDS = ['messages', 'journal'] as const
export type Slot4NavId = (typeof SLOT_4_NAV_IDS)[number]

/**
 * Resolve bottom-nav slot 4.
 * - Web: Journal when `journaling` feature flag is on; otherwise Messages (keeps five tabs).
 * - Expo: Messages until Journal ships on mobile (`mobileJournalEnabled`).
 */
export function resolveSlot4NavId(options: {
  journalingEnabled: boolean
  /** Expo: set true only when Journal screen is in the tab navigator */
  mobileJournalEnabled?: boolean
}): Slot4NavId {
  if (options.mobileJournalEnabled && options.journalingEnabled) {
    return 'journal'
  }
  if (options.mobileJournalEnabled === undefined && options.journalingEnabled) {
    // Web / unspecified platform: journaling flag alone selects Journal
    return 'journal'
  }
  return 'messages'
}

/** Canonical member destinations (web paths / mobile route names) */
export const MEMBER_DESTINATIONS = {
  home: { webPath: '/home', mobileRoute: 'Home' },
  groups: { webPath: '/groups', mobileRoute: 'Groups' },
  tasks: { webPath: '/tasks?filter=today', mobileRoute: 'Tasks' },
  messages: { webPath: '/messages', mobileRoute: 'Messages' },
  journal: { webPath: '/journal', mobileRoute: 'Journal' },
  profile: { webPath: '/profile', mobileRoute: 'Profile' },
  curriculum: { webPath: '/curriculum', mobileRoute: 'Curriculum' },
  events: { webPath: '/events', mobileRoute: 'Events' },
} as const

/** Feed is removed from IA on both platforms */
export const REMOVED_FROM_IA = ['feed'] as const

/** Admin console destinations — see `adminConsole.ts` for section IA */
export const ADMIN_DESTINATIONS = {
  orgAdmin: { webPath: '/admin', mobileRoute: 'AdminDashboard' },
  moderation: { webPath: '/admin/moderation', mobileRoute: 'AdminDashboard' },
  superAdmin: { webPath: '/super-admin', mobileRoute: 'AdminDashboard' },
  enterpriseAdmin: { webPath: '/enterprise-admin', mobileRoute: 'AdminDashboard' },
} as const

export const BRAND_COLORS = {
  navy: '#0B2345',
  navyDark: '#071A33',
  gold: '#F2C14E',
  goldDark: '#D4A73C',
  goldSoft: '#F8E7B0',
} as const
